import { useId, useMemo, useState, type FormEvent } from 'react';
import { Search, X } from 'lucide-react';
import { Carte, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import {
  useAjouterMotBloque,
  useEssaiMotsBloques,
  useRetirerMotBloque,
  type MotBloque,
  type VueMotsBloques,
} from '../../lib/useParametrage';
import { dateReunion } from '../../lib/useFormateurs';
import { CLASSE_BOUTON_ENREGISTRER, messageErreur } from './commun';

const CLASSE_CHAMP =
  'mt-1 min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function sansAccents(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}

/**
 * Mots bloqués (TF-484) : la liste que l'API compare aux textes écrits pour
 * d'autres. L'écran ne fait qu'afficher la liste et la règle ; l'essai d'un
 * texte est calculé par l'API, la seule à connaître la règle.
 */
export function OngletMotsBloques({ motsBloques }: { motsBloques: VueMotsBloques }): JSX.Element {
  return (
    <>
      <CarteListe motsBloques={motsBloques} />
      <CarteEssai vide={motsBloques.mots.length === 0} />
    </>
  );
}

function CarteListe({ motsBloques }: { motsBloques: VueMotsBloques }): JSX.Element {
  const toast = useToast();
  const ajouter = useAjouterMotBloque();
  const retirer = useRetirerMotBloque();
  const id = useId();
  const { mots, limites } = motsBloques;
  const [saisie, setSaisie] = useState('');
  const [filtre, setFiltre] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const affiches = useMemo(() => {
    const cherche = sansAccents(filtre.trim());
    return cherche ? mots.filter((mot) => sansAccents(mot.mot).includes(cherche)) : mots;
  }, [mots, filtre]);

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const mot = saisie.trim();
    if (!mot) return;
    setErreur(null);
    ajouter.mutate(mot, {
      onSuccess: () => {
        setSaisie('');
        toast(`« ${mot} » ajouté à la liste`);
      },
      onError: (refus) => setErreur(messageErreur(refus, 'Le mot n’a pas pu être ajouté.')),
    });
  };

  const retrait = (mot: MotBloque): void => {
    setErreur(null);
    retirer.mutate(mot.id, {
      onSuccess: () => toast(`« ${mot.mot} » retiré de la liste`),
      onError: (refus) => setErreur(messageErreur(refus, 'Le mot n’a pas pu être retiré.')),
    });
  };

  return (
    <Carte titre="Mots bloqués">
      <div className="space-y-4 px-4 py-4 sm:px-6">
        <div className="space-y-2 text-sm text-codialis-gray">
          <p>
            Un texte qui contient l’un de ces mots n’est pas enregistré, et son auteur voit le mot en cause : messages
            de la messagerie, questions et réponses de la discussion des cours, réclamations, réponses libres aux
            questionnaires, commentaires de correction.
          </p>
          <p>
            Les mots sont comparés entiers, sans tenir compte des majuscules, des accents ni de la ponctuation, et le
            pluriel en s ou x est compris : « con » ne bloque pas « contrat ». Un astérisque final bloque tout mot qui
            commence pareil (« encul* », au moins {limites.prefixe} lettres avant). Une expression de plusieurs mots
            est bloquée quand ses mots se suivent. Les autres façons d’écrire un mot (« c0nnard ») s’ajoutent comme
            des mots à part.
          </p>
        </div>

        <form onSubmit={envoyer} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <label htmlFor={`${id}-mot`} className="min-w-0 flex-1 text-xs font-semibold text-codialis-navy">
            Mot ou expression à bloquer
            <input
              id={`${id}-mot`}
              type="text"
              autoComplete="off"
              maxLength={limites.max}
              value={saisie}
              onChange={(evenement) => {
                setErreur(null);
                setSaisie(evenement.target.value);
              }}
              className={CLASSE_CHAMP}
            />
          </label>
          <button
            type="submit"
            className={`${CLASSE_BOUTON_ENREGISTRER} shrink-0`}
            disabled={saisie.trim().length < limites.min || ajouter.isPending || mots.length >= limites.nombre}
          >
            {ajouter.isPending ? 'Ajout…' : 'Ajouter'}
          </button>
        </form>
        {erreur && <MessageErreur message={erreur} />}

        <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-semibold text-codialis-navy">
            {mots.length === 0
              ? 'Aucun mot bloqué : aucun texte n’est filtré.'
              : `${mots.length} mot${mots.length > 1 ? 's' : ''} bloqué${mots.length > 1 ? 's' : ''} sur ${limites.nombre.toLocaleString('fr-FR')} possibles`}
          </p>
          {mots.length > 0 && (
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray" aria-hidden />
              <input
                type="search"
                aria-label="Chercher dans la liste des mots bloqués"
                placeholder="Chercher un mot"
                value={filtre}
                onChange={(evenement) => setFiltre(evenement.target.value)}
                className="min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
              />
            </div>
          )}
        </div>

        {mots.length > 0 && affiches.length === 0 && (
          <p className="text-sm text-codialis-gray">Aucun mot de la liste ne correspond à « {filtre.trim()} ».</p>
        )}
        {affiches.length > 0 && (
          <ul className="flex flex-wrap gap-2" aria-label="Mots bloqués">
            {affiches.map((mot) => {
              const enCours = retirer.isPending && retirer.variables === mot.id;
              return (
                <li
                  key={mot.id}
                  className="flex min-w-0 max-w-full items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-3 pr-1 text-sm text-codialis-navy"
                  title={`Ajouté${mot.ajoutePar ? ` par ${mot.ajoutePar}` : ''} le ${dateReunion(mot.ajouteLe)}`}
                >
                  <span className="min-w-0 truncate">{mot.mot}</span>
                  <button
                    type="button"
                    onClick={() => retrait(mot)}
                    disabled={enCours}
                    aria-label={`Retirer « ${mot.mot} » de la liste`}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-codialis-gray transition hover:bg-gray-100 hover:text-red-700 disabled:opacity-50 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Carte>
  );
}

function CarteEssai({ vide }: { vide: boolean }): JSX.Element {
  const essai = useEssaiMotsBloques();
  const id = useId();
  const [texte, setTexte] = useState('');

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (!texte.trim()) return;
    essai.mutate(texte);
  };

  return (
    <Carte titre="Essayer un texte">
      <form onSubmit={envoyer} className="space-y-3 px-4 py-4 sm:px-6">
        <p className="text-sm text-codialis-gray">
          Pour vérifier ce que la liste bloque, et ce qu’elle laisse passer. Rien n’est enregistré ni envoyé.
        </p>
        <label htmlFor={`${id}-texte`} className="block text-xs font-semibold text-codialis-navy">
          Texte à essayer
          <textarea
            id={`${id}-texte`}
            rows={3}
            maxLength={4000}
            value={texte}
            onChange={(evenement) => {
              essai.reset();
              setTexte(evenement.target.value);
            }}
            className={`${CLASSE_CHAMP} resize-y`}
          />
        </label>
        <div className="flex justify-end">
          <button type="submit" className={CLASSE_BOUTON_ENREGISTRER} disabled={!texte.trim() || essai.isPending}>
            {essai.isPending ? 'Essai…' : 'Essayer'}
          </button>
        </div>
        <div aria-live="polite">
          {essai.data &&
            (essai.data.trouves.length === 0 ? (
              <p className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy">
                {vide ? 'La liste est vide : rien ne serait bloqué.' : 'Rien ne serait bloqué.'}
              </p>
            ) : (
              <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                Ce texte serait refusé : {essai.data.trouves.map((trouve) => `« ${trouve} »`).join(', ')}.
              </p>
            ))}
        </div>
        {essai.error && <MessageErreur message={messageErreur(essai.error, 'L’essai n’a pas pu être fait.')} />}
      </form>
    </Carte>
  );
}
